// SQL Agent API Service for analytics service integration

const ANALYTICS_SERVICE_BASE_URL = import.meta.env.PROD 
  ? ''  // Use relative URLs in production (goes through Vercel proxy)
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

export interface SqlAgentAskRequest {
  question: string;
  context?: string;
  include_schema?: boolean;
  limit?: number;
  execute?: boolean;
  explain_only?: boolean;
}

export interface SqlAgentAskResponse {
  question: string;
  sql_query?: string;
  results?: any[];
  rows?: any[]; // Alternative to results, some APIs use "rows"
  error?: string;
  explanation?: string;
  metadata?: {
    execution_time_ms?: number;
    row_count?: number;
    tables_used?: string[];
  };
  columns?: string[];
  warnings?: string[];
  row_count?: number; // Some APIs put row_count at top level
  execution_time_ms?: number; // Some APIs put execution_time_ms at top level
}

export interface TableInfo {
  table_name: string;
  columns: Array<{
    column_name: string;
    data_type: string;
    is_nullable: boolean;
    column_default?: string;
  }>;
  row_count?: number;
  relationships?: Array<{
    foreign_table: string;
    foreign_column: string;
    local_column: string;
  }>;
}

export interface ExampleQuery {
  title: string;
  description: string;
  query: string;
  category: string;
}

class SqlAgentApiService {
  private static getHeaders(): HeadersInit {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    try {
      const token = localStorage.getItem('series_analytics_token');
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch {}
    return headers;
  }

  static async askQuestion(request: SqlAgentAskRequest): Promise<SqlAgentAskResponse> {
    try {
      const response = await fetch(`${ANALYTICS_SERVICE_BASE_URL}/api/v1/analytics/sql-agent/ask`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`SQL Agent error: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to ask SQL agent:', error);
      throw error;
    }
  }

  static async getSchemaInfo(): Promise<any> {
    try {
      const response = await fetch(`${ANALYTICS_SERVICE_BASE_URL}/api/v1/analytics/sql-agent/schema`, {
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to get schema info: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get schema info:', error);
      throw error;
    }
  }

  static async getTables(): Promise<string[]> {
    try {
      const response = await fetch(`${ANALYTICS_SERVICE_BASE_URL}/api/v1/analytics/sql-agent/schema/tables`, {
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to get tables: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get tables:', error);
      throw error;
    }
  }

  static async getTableDetails(tableName: string): Promise<TableInfo> {
    try {
      const response = await fetch(`${ANALYTICS_SERVICE_BASE_URL}/api/v1/analytics/sql-agent/schema/tables/${tableName}`, {
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to get table details: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get table details:', error);
      throw error;
    }
  }

  static async validateSql(sql: string): Promise<{ valid: boolean; error?: string }> {
    try {
      const response = await fetch(`${ANALYTICS_SERVICE_BASE_URL}/api/v1/analytics/sql-agent/validate`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ sql }),
      });

      if (!response.ok) {
        throw new Error(`Failed to validate SQL: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to validate SQL:', error);
      throw error;
    }
  }

  static async getExampleQueries(): Promise<ExampleQuery[]> {
    try {
      const response = await fetch(`${ANALYTICS_SERVICE_BASE_URL}/api/v1/analytics/sql-agent/examples`, {
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to get examples: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get example queries:', error);
      throw error;
    }
  }

  static async checkHealth(): Promise<{ status: string; message?: string }> {
    try {
      const response = await fetch(`${ANALYTICS_SERVICE_BASE_URL}/api/v1/analytics/sql-agent/health`, {
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Health check failed:', error);
      return { status: 'error', message: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
}

export default SqlAgentApiService;
